import React, { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './ResultsPanel.css';

const ResultsPanel = ({ results }) => {
    const [showBreakdown, setShowBreakdown] = useState(true);

    if (!results) return null;

    const cocRoi = results.cashOnCashROI || 0;
    const ratingClass = cocRoi >= 5 ? 'excellent' : cocRoi >= 0 ? 'marginal' : 'negative';

    return (
        <div className="results-panel">
            {/* Hero: Cash on Cash ROI */}
            <div className={`rp-hero ${ratingClass}`}>
                <div className="rp-hero-value">{cocRoi.toFixed(1)}%</div>
                <div className="rp-hero-label">Cash on Cash ROI</div>
                <div
                    className="rp-hero-rating"
                    style={{ color: results.dealRatingColor }}
                >
                    {results.dealRating}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="rp-kpi-grid">
                <div className="rp-kpi">
                    <span className="rp-kpi-label">Monthly Cash Flow</span>
                    <span className={`rp-kpi-value ${results.monthlyCashFlow >= 0 ? 'positive' : 'negative-val'}`}>
                        {formatCurrency(results.monthlyCashFlow)}
                    </span>
                </div>
                <div className="rp-kpi">
                    <span className="rp-kpi-label">Monthly Mortgage</span>
                    <span className="rp-kpi-value">{formatCurrency(results.monthlyMortgage)}</span>
                </div>
                <div className="rp-kpi">
                    <span className="rp-kpi-label">NOI (Annual)</span>
                    <span className="rp-kpi-value">{formatCurrency(results.netOperatingIncome)}</span>
                </div>
                <div className="rp-kpi">
                    <span className="rp-kpi-label">Cap Rate</span>
                    <span className="rp-kpi-value">{(results.capRate || 0).toFixed(1)}%</span>
                </div>
                <div className="rp-kpi">
                    <span className="rp-kpi-label">Cash to Close</span>
                    <span className="rp-kpi-value">{formatCurrency(results.cashInvested)}</span>
                </div>
                <div className="rp-kpi">
                    <span className="rp-kpi-label">Total ROI</span>
                    <span className="rp-kpi-value">{(results.totalROI || 0).toFixed(1)}%</span>
                </div>
            </div>

            {/* Financial Breakdown (collapsible) */}
            <button
                className="rp-breakdown-toggle"
                onClick={() => setShowBreakdown(!showBreakdown)}
            >
                <span>Financial Breakdown</span>
                {showBreakdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {showBreakdown && (
                <div className="rp-breakdown">
                    <div className="rp-breakdown-row">
                        <span>Gross Rental Income</span>
                        <span>{formatCurrency(results.grossRentalIncome)}</span>
                    </div>
                    <div className="rp-breakdown-row">
                        <span>Effective Gross Income</span>
                        <span>{formatCurrency(results.effectiveGrossIncome)}</span>
                    </div>
                    <div className="rp-breakdown-row expense">
                        <span>Total Operating Expenses</span>
                        <span>-{formatCurrency(results.totalOperatingExpenses)}</span>
                    </div>
                    <div className="rp-breakdown-row bold">
                        <span>Net Operating Income</span>
                        <span>{formatCurrency(results.netOperatingIncome)}</span>
                    </div>
                    <div className="rp-breakdown-row expense">
                        <span>Annual Debt Service</span>
                        <span>-{formatCurrency(results.annualDebtService)}</span>
                    </div>
                    <div className="rp-breakdown-row bold highlight">
                        <span>Annual Cash Flow</span>
                        <span className={results.annualCashFlow >= 0 ? 'positive' : 'negative-val'}>
                            {formatCurrency(results.annualCashFlow)}
                        </span>
                    </div>

                    <div className="rp-breakdown-divider" />

                    <div className="rp-breakdown-row">
                        <span>Cash Invested</span>
                        <span>{formatCurrency(results.cashInvested)}</span>
                    </div>
                    <div className="rp-breakdown-row">
                        <span>Loan Amount</span>
                        <span>{formatCurrency(results.loanAmount)}</span>
                    </div>
                    <div className="rp-breakdown-row">
                        <span>Down Payment</span>
                        <span>{formatCurrency(results.downPaymentAmount)}</span>
                    </div>
                    <div className="rp-breakdown-row">
                        <span>Year 1 Principal Paid</span>
                        <span>{formatCurrency(results.principalPaidYear1)}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResultsPanel;
